/*
 * Transport — Plane IV. The ONLY live plane. The flagship.
 *
 * Everything here is bound to what the running sidecar ACTUALLY exposes:
 *   - GET /v1/metrics          → live time-series tiles + sparklines (poll-built)
 *   - GET /v1/dispatch/{id}    → the lookup-by-request_id drill-in
 *   - GET /v1/health/{probe}   → the health strip
 *
 * There is NO "list dispatches" endpoint, so this view invents none. Throughput
 * is metrics-driven; individual records are reached only by request_id.
 *
 * Provability law: every tile and badge links to its proof. A metric's proof is
 * the exact /v1/metrics line it was parsed from; a dispatch's proof is its
 * RFC 9421 signature-verified verdict plus its audit-pair hashes. The all-zeros
 * ack hash is shown HONESTLY as "queued, not yet delivered" — store-and-forward
 * in flight, never dressed up as delivered.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { getDispatch, MeshApiError, getHealthProbe } from "../api/client";
import {
  useMetricsPoll,
  useDerivedSeries,
  sumMetric,
  histogramQuantile,
} from "../api/metrics";
import {
  TRANSPORT_METRICS,
  isDelivered,
  ZERO_HASH,
} from "../api/types";
import type {
  Role,
  Claim,
  DispatchReceipt,
  HealthProbe,
  MetricSample,
} from "../api/types";
import { MetricTile } from "../components/MetricTile";
import { SpecRef } from "../components/SpecRef";
import { Hash } from "../components/Hash";
import { VerifyBadge } from "../components/VerifyBadge";
import { ProvabilityDrawer } from "../components/ProvabilityDrawer";
import { PlaneState } from "../components/PlaneState";

const POLL_MS = 5000;

/* The exact metric value a tile shows, plus the raw /v1/metrics line that
 * proves it. Selectors run over the latest parsed samples each poll. */
interface SeriesSpec {
  key: string;
  label: string;
  unit?: string;
  accent: string;
  /** Loud when a non-zero value is a failure surface (replays / dead letters). */
  loud?: boolean;
  /** The Prometheus family name(s) this tile is derived from. */
  metricNames: string[];
  /** Pull the scalar this tile displays from the samples. */
  select: (s: MetricSample[]) => number;
  /** Short note rendered in the proof drawer. */
  note: string;
  specRef: string;
}

const TM = TRANSPORT_METRICS;

const SERIES: SeriesSpec[] = [
  {
    key: "requests",
    label: "Requests dispatched",
    unit: "req",
    accent: "var(--ember)",
    metricNames: [TM.requestsTotal],
    select: (s) => sumMetric(s, TM.requestsTotal),
    note: "Total signed dispatches accepted by the sidecar (counter).",
    specRef: "Part IV §24",
  },
  {
    key: "sigverify",
    label: "Signature verifications",
    unit: "ver",
    accent: "var(--teal)",
    metricNames: [TM.signatureVerifications],
    select: (s) => sumMetric(s, TM.signatureVerifications),
    note: "RFC 9421 signature verifications performed (counter).",
    specRef: "Part IV §27",
  },
  {
    key: "queue",
    label: "Queue pending",
    unit: "msg",
    accent: "var(--ember)",
    metricNames: [TM.queuePending],
    select: (s) => sumMetric(s, TM.queuePending),
    note: "Store-and-forward backlog awaiting delivery (gauge).",
    specRef: "Part IV §29",
  },
  {
    key: "replays",
    label: "Replay detections",
    unit: "hit",
    accent: "var(--red)",
    loud: true,
    metricNames: [TM.replayDetections],
    select: (s) => sumMetric(s, TM.replayDetections),
    note: "Replayed requests rejected on nonce/window violation (counter). Non-zero is loud.",
    specRef: "Part IV §27.4",
  },
  {
    key: "deadletters",
    label: "Dead letters",
    unit: "msg",
    accent: "var(--red)",
    loud: true,
    metricNames: [TM.deadLetters],
    select: (s) => sumMetric(s, TM.deadLetters),
    note: "Dispatches that exhausted store-and-forward retries (counter). Non-zero is loud.",
    specRef: "Part IV §29.3",
  },
  {
    key: "latency",
    label: "Latency p95",
    unit: "ms",
    accent: "var(--indigo)",
    metricNames: [TM.latencyBucket, TM.latencyCount],
    select: (s) => {
      const q = histogramQuantile(s, TM.latencyBucket, TM.latencyCount, 0.95);
      return q === undefined ? NaN : q * 1000; // seconds → ms
    },
    note: "95th-percentile dispatch latency, estimated from histogram buckets (linear interpolation within bucket).",
    specRef: "Part IV §31",
  },
];

/* Secondary integrity counters — small inline row, also poll-derived. */
const INTEGRITY: SeriesSpec[] = [
  {
    key: "orphans",
    label: "Audit-pair orphans",
    unit: "pair",
    accent: "var(--red)",
    loud: true,
    metricNames: [TM.auditPairOrphans],
    select: (s) => sumMetric(s, TM.auditPairOrphans),
    note: "Open audit entries never paired by an ACK — a chain-integrity smell (counter). Non-zero is loud.",
    specRef: "Part VI §38",
  },
  {
    key: "peerlookups",
    label: "Peer key lookups",
    unit: "lk",
    accent: "var(--indigo)",
    metricNames: [TM.peerKeyLookups],
    select: (s) => sumMetric(s, TM.peerKeyLookups),
    note: "Peer signing-key resolutions for federation routing (counter).",
    specRef: "Part IV §30",
  },
];

export default function Transport({ role }: { role: Role }) {
  const { samples, error, loading, lastUpdated, refresh } = useMetricsPoll(POLL_MS);
  const [claim, setClaim] = useState<Claim | null>(null);

  return (
    <section>
      <header className="view-header">
        <div className="view-header__eyebrow">
          <span className="accent-ember">Transport</span>
          <span><SpecRef text="Plane IV · §24–§31 · LIVE" /></span>
        </div>
        <h1 className="view-header__title">Signed dispatch &amp; store-and-forward</h1>
        <p className="view-header__sub">
          Live from <code>/v1/metrics</code> — there is no list endpoint, so
          throughput is metrics-driven and individual records are reached by{" "}
          <code>request_id</code>. Every tile links to the exact line that
          proves it. Role: <strong>{role}</strong>.
        </p>
      </header>

      <HealthStrip />

      <MetricsBoard
        samples={samples}
        error={error}
        loading={loading}
        lastUpdated={lastUpdated}
        refresh={refresh}
        onInspect={setClaim}
      />

      <DispatchLookup onInspect={setClaim} />

      <ProvabilityDrawer claim={claim} onClose={() => setClaim(null)} />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Health strip — /v1/health/{live,ready,startup}                      */
/* ------------------------------------------------------------------ */

const PROBES: HealthProbe[] = ["live", "ready", "startup"];

function HealthStrip() {
  const [probes, setProbes] = useState<Record<HealthProbe, boolean | null>>({
    live: null,
    ready: null,
    startup: null,
  });
  const aliveRef = useRef(true);

  const poll = useCallback(async () => {
    const entries = await Promise.all(
      PROBES.map(async (p) => [p, await getHealthProbe(p)] as const),
    );
    if (!aliveRef.current) return;
    setProbes((prev) => {
      const next = { ...prev };
      for (const [p, ok] of entries) next[p] = ok;
      return next;
    });
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    void poll();
    const id = setInterval(() => void poll(), POLL_MS);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
  }, [poll]);

  return (
    <div
      className="panel"
      style={{
        marginBottom: "var(--space-5)",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "var(--space-3) var(--space-5)",
        padding: "var(--space-4) var(--space-5)",
      }}
    >
      <span
        className="mono"
        style={{
          fontSize: "11px",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--fg-faint)",
        }}
      >
        Sidecar health
      </span>
      {PROBES.map((p) => (
        <ProbeChip key={p} probe={p} ok={probes[p]} />
      ))}
      <span
        className="mono"
        style={{ fontSize: "11px", color: "var(--fg-faint)", marginLeft: "auto" }}
      >
        /v1/health/&#123;live,ready,startup&#125;
      </span>
    </div>
  );
}

function ProbeChip({ probe, ok }: { probe: HealthProbe; ok: boolean | null }) {
  const accent =
    ok === null ? "var(--gold)" : ok ? "var(--teal)" : "var(--red)";
  const word = ok === null ? "probing" : ok ? "up" : "DOWN";
  const loud = ok === false;
  return (
    <span
      role={loud ? "alert" : "status"}
      aria-label={`${probe} probe ${word}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        fontFamily: "var(--font-mono)",
        fontSize: "12px",
        minHeight: "32px",
        padding: "0 4px",
        color: loud ? "var(--red)" : "var(--fg-muted)",
        fontWeight: loud ? 600 : 400,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: "9px",
          height: "9px",
          borderRadius: "50%",
          background: accent,
          boxShadow: loud ? "0 0 0 3px var(--red-dim)" : "none",
          flex: "0 0 auto",
        }}
      />
      {probe}
      <span style={{ color: accent }}>{word}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Metrics board — the live tiles                                      */
/* ------------------------------------------------------------------ */

/* One live tile. Owns its own rolling series so hooks stay top-level and the
 * call count is fixed (no hooks-in-a-loop). */
function LiveMetricTile({
  spec,
  samples,
  withSparkline,
  onInspect,
}: {
  spec: SeriesSpec;
  samples: MetricSample[] | null;
  withSparkline?: boolean;
  onInspect: (c: Claim) => void;
}) {
  const series = useDerivedSeries(samples, spec.select);
  const value = samples ? readScalar(spec, samples) : null;
  return (
    <MetricTile
      label={spec.label}
      value={value}
      unit={spec.unit}
      accent={spec.accent}
      loud={spec.loud}
      series={withSparkline ? series : undefined}
      source={spec.metricNames[0]}
      onInspect={() => onInspect(buildMetricClaim(spec, value, samples))}
    />
  );
}

function MetricsBoard({
  samples,
  error,
  loading,
  lastUpdated,
  refresh,
  onInspect,
}: {
  samples: MetricSample[] | null;
  error: MeshApiError | null;
  loading: boolean;
  lastUpdated: number | null;
  refresh: () => void;
  onInspect: (c: Claim) => void;
}) {
  // Hard offline: never reached a first successful poll AND we have an error.
  if (error && !samples) {
    return (
      <div style={{ marginBottom: "var(--space-6)" }}>
        <PlaneState
          kind="error"
          title="Metrics unreadable — sidecar not answering"
          message={`Could not read /v1/metrics (${error.status === 0 ? "network/unreachable" : `HTTP ${error.status}`}). Throughput cannot be shown, and the console will not fabricate it.`}
          endpoints={["GET /v1/metrics"]}
          specRef="Part IV §31"
          onRetry={refresh}
        />
      </div>
    );
  }

  return (
    <section style={{ marginBottom: "var(--space-6)" }} aria-label="Live transport metrics">
      <BoardHeader
        loading={loading}
        lastUpdated={lastUpdated}
        stale={Boolean(error && samples)}
        refresh={refresh}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: "var(--space-4)",
        }}
      >
        {SERIES.map((spec) => (
          <LiveMetricTile
            key={spec.key}
            spec={spec}
            samples={samples}
            withSparkline
            onInspect={onInspect}
          />
        ))}
      </div>

      <IntegrityRow samples={samples} onInspect={onInspect} />
    </section>
  );
}

function BoardHeader({
  loading,
  lastUpdated,
  stale,
  refresh,
}: {
  loading: boolean;
  lastUpdated: number | null;
  stale: boolean;
  refresh: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
        marginBottom: "var(--space-4)",
        flexWrap: "wrap",
      }}
    >
      <h2 style={{ fontSize: "18px" }}>Throughput &amp; integrity</h2>
      <span
        className="mono"
        style={{ fontSize: "11px", color: stale ? "var(--red)" : "var(--fg-faint)" }}
      >
        {stale
          ? "● last poll failed — values frozen"
          : loading
            ? "◴ polling…"
            : lastUpdated
              ? `● live · updated ${fmtAgo(lastUpdated)}`
              : "◴ awaiting first poll"}
      </span>
      <button
        type="button"
        onClick={refresh}
        aria-label="Refresh metrics now"
        style={{
          marginLeft: "auto",
          minHeight: "36px",
          padding: "0 14px",
          borderRadius: "var(--radius-sm)",
          border: "1px solid var(--hair-strong)",
          background: "var(--bg)",
          color: "var(--fg)",
          fontFamily: "var(--font-mono)",
          fontSize: "12px",
        }}
      >
        Refresh
      </button>
    </div>
  );
}

function IntegrityRow({
  samples,
  onInspect,
}: {
  samples: MetricSample[] | null;
  onInspect: (c: Claim) => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: "var(--space-4)",
        marginTop: "var(--space-4)",
      }}
    >
      {INTEGRITY.map((spec) => (
        <LiveMetricTile
          key={spec.key}
          spec={spec}
          samples={samples}
          onInspect={onInspect}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dispatch lookup — the request_id drill-in                           */
/* ------------------------------------------------------------------ */

type LookupState =
  | { status: "idle" }
  | { status: "loading"; id: string }
  | { status: "ok"; id: string; receipt: DispatchReceipt }
  | { status: "error"; id: string; error: MeshApiError };

function DispatchLookup({ onInspect }: { onInspect: (c: Claim) => void }) {
  // Deep-link support: #/transport/<request_id> prefills + auto-looks-up.
  const initialId = readHashTail();
  const [input, setInput] = useState(initialId);
  const [state, setState] = useState<LookupState>({ status: "idle" });
  const aliveRef = useRef(true);

  const lookup = useCallback(async (rawId: string) => {
    const id = rawId.trim();
    if (!id) return;
    setState({ status: "loading", id });
    // Reflect into the hash so the lookup is shareable / bookmarkable.
    if (readHashTail() !== id) {
      window.location.hash = `/transport/${encodeURIComponent(id)}`;
    }
    try {
      const receipt = await getDispatch(id);
      if (!aliveRef.current) return;
      setState({ status: "ok", id, receipt });
    } catch (e) {
      if (!aliveRef.current) return;
      const error =
        e instanceof MeshApiError ? e : new MeshApiError("lookup failed", 0, id);
      setState({ status: "error", id, error });
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    if (initialId) void lookup(initialId);
    return () => {
      aliveRef.current = false;
    };
    // run once on mount for the deep-link
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section style={{ marginBottom: "var(--space-6)" }} aria-label="Dispatch lookup">
      <h2 style={{ fontSize: "18px", marginBottom: "var(--space-2)" }}>
        Lookup a dispatch
      </h2>
      <p
        style={{
          color: "var(--fg-muted)",
          marginTop: 0,
          marginBottom: "var(--space-4)",
          maxWidth: "64ch",
        }}
      >
        No list endpoint exists. Enter a <code>request_id</code> to fetch its
        receipt from <code>/v1/dispatch/&#123;request_id&#125;</code> and inspect
        its signature verdict and audit-pair hashes.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void lookup(input);
        }}
        style={{
          display: "flex",
          gap: "var(--space-3)",
          flexWrap: "wrap",
          marginBottom: "var(--space-4)",
        }}
      >
        <label htmlFor="dispatch-id" className="sr-only">
          Dispatch request_id
        </label>
        <input
          id="dispatch-id"
          name="request_id"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="request_id (e.g. 4f3c…)"
          autoComplete="off"
          spellCheck={false}
          style={{
            flex: "1 1 320px",
            minHeight: "var(--tap)",
            padding: "0 var(--space-3)",
            background: "var(--bg)",
            color: "var(--fg)",
            border: "1px solid var(--hair-strong)",
            borderRadius: "var(--radius-sm)",
            fontFamily: "var(--font-mono)",
            fontSize: "13px",
          }}
        />
        <button
          type="submit"
          disabled={!input.trim() || state.status === "loading"}
          style={{
            minHeight: "var(--tap)",
            padding: "0 var(--space-5)",
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--ember)",
            background: "var(--ember-dim)",
            color: "var(--ember)",
            fontFamily: "var(--font-mono)",
            fontSize: "13px",
            opacity: !input.trim() || state.status === "loading" ? 0.5 : 1,
          }}
        >
          {state.status === "loading" ? "Looking up…" : "Lookup"}
        </button>
      </form>

      {state.status === "loading" && (
        <PlaneState
          kind="loading"
          title="Fetching dispatch receipt…"
          message={`GET /v1/dispatch/${state.id}`}
        />
      )}

      {state.status === "error" && (
        <PlaneState
          kind="error"
          title={
            state.error.status === 404
              ? "No dispatch with that request_id"
              : "Dispatch lookup failed"
          }
          message={
            state.error.status === 404
              ? `The sidecar has no receipt for "${state.id}". Request IDs are returned by POST /v1/dispatch; there is no list endpoint to browse.`
              : `${state.error.status === 0 ? "Network/unreachable" : `HTTP ${state.error.status}`} from /v1/dispatch/${state.id}.`
          }
          endpoints={[`GET /v1/dispatch/${state.id}`]}
          specRef="Part IV §28"
          onRetry={() => void lookup(state.id)}
        />
      )}

      {state.status === "ok" && (
        <DispatchReceiptCard receipt={state.receipt} onInspect={onInspect} />
      )}
    </section>
  );
}

function DispatchReceiptCard({
  receipt,
  onInspect,
}: {
  receipt: DispatchReceipt;
  onInspect: (c: Claim) => void;
}) {
  const delivered = isDelivered(receipt);
  const sigVerdict = receipt.signature_verified ? "verified" : "failed";

  return (
    <div className="panel" style={{ display: "grid", gap: "var(--space-4)" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--space-3)",
          flexWrap: "wrap",
        }}
      >
        <Hash
          label="request_id"
          value={receipt.request_id}
          head={12}
          tail={8}
          accent="var(--fg)"
        />
        <VerifyBadge
          state={sigVerdict}
          label={
            receipt.signature_verified ? "Signature verified" : "Signature FAILED"
          }
          onInspect={() => onInspect(buildSignatureClaim(receipt))}
        />
      </div>

      {/* Store-and-forward delivery state — modelled honestly. */}
      <DeliveryState receipt={receipt} delivered={delivered} onInspect={onInspect} />

      <dl style={{ display: "grid", gap: "var(--space-3)", margin: 0 }}>
        <Row label="status">
          <span className="mono" style={{ fontSize: "12.5px" }}>
            {receipt.status || "—"}
          </span>
        </Row>
        <Row label="audit_open_hash">
          <Hash
            value={receipt.audit_open_hash}
            accent="var(--teal)"
            onInspect={() =>
              onInspect(buildAuditClaim("Opening audit entry", receipt.audit_open_hash, false))
            }
          />
        </Row>
        <Row label="audit_ack_hash">
          <Hash
            value={receipt.audit_ack_hash}
            accent={delivered ? "var(--teal)" : undefined}
            zeroMeans="not yet acked — queued in store-and-forward"
            onInspect={
              delivered
                ? () =>
                    onInspect(
                      buildAuditClaim("ACK audit entry", receipt.audit_ack_hash, true),
                    )
                : undefined
            }
          />
        </Row>
      </dl>
    </div>
  );
}

function DeliveryState({
  receipt,
  delivered,
  onInspect,
}: {
  receipt: DispatchReceipt;
  delivered: boolean;
  onInspect: (c: Claim) => void;
}) {
  const accent = delivered ? "var(--teal)" : "var(--ember)";
  const label = delivered ? "Delivered" : "Queued — store-and-forward in flight";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
        padding: "var(--space-3) var(--space-4)",
        border: `1px solid ${delivered ? "rgba(var(--teal-rgb),0.3)" : "rgba(var(--ember-rgb),0.3)"}`,
        borderRadius: "var(--radius-sm)",
        background: delivered ? "var(--teal-dim)" : "var(--ember-dim)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          background: accent,
          flex: "0 0 auto",
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div className="mono" style={{ fontSize: "13px", color: accent, fontWeight: 500 }}>
          {label}
        </div>
        <div style={{ fontSize: "12.5px", color: "var(--fg-muted)", marginTop: "2px" }}>
          {delivered
            ? "Store-and-forward completed: the ACK audit entry carries a real hash, pairing the opening entry."
            : "The ACK hash is the all-zeros sentinel — delivery has not completed, so the audit pair is not yet closed. This is shown as queued, not delivered."}
        </div>
      </div>
      <VerifyBadge
        state={delivered ? "verified" : "pending"}
        size="sm"
        label={delivered ? "acked" : "queued"}
        onInspect={() => onInspect(buildDeliveryClaim(receipt, delivered))}
      />
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "var(--space-3)",
        alignItems: "baseline",
      }}
    >
      <dt
        className="mono"
        style={{ fontSize: "12px", color: "var(--fg-faint)", minWidth: "130px" }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Claim builders — proof receipts                                     */
/* ------------------------------------------------------------------ */

function buildMetricClaim(
  spec: SeriesSpec,
  value: number | null,
  samples: MetricSample[] | null,
): Claim {
  const readable = value !== null && Number.isFinite(value);
  // Reconstruct the matching /v1/metrics lines so the user sees the raw proof.
  const lines = samples
    ? spec.metricNames.flatMap((name) =>
        samples
          .filter((s) => s.name === name)
          .map((s) => `${renderSample(s)}`),
      )
    : [];

  return {
    label: spec.label,
    verdict: readable ? "verified" : "failed",
    evidence: {
      specRef: spec.specRef,
      source: `GET /v1/metrics → ${spec.metricNames.join(", ")}`,
      signatureBase: lines.length > 0 ? lines.join("\n") : undefined,
      detail: [
        {
          label: "value",
          value: readable
            ? `${value}${spec.unit ? ` ${spec.unit}` : ""}`
            : "unreadable — not fabricated",
        },
        { label: "kind", value: spec.note },
      ],
    },
  };
}

function buildSignatureClaim(r: DispatchReceipt): Claim {
  return {
    label: "RFC 9421 dispatch signature",
    verdict: r.signature_verified ? "verified" : "failed",
    evidence: {
      algorithm: "ed25519",
      specRef: "Part IV §27 — RFC 9421 message signatures",
      source: `GET /v1/dispatch/${r.request_id}`,
      detail: [
        { label: "request_id", value: r.request_id },
        {
          label: "signature_verified",
          value: r.signature_verified ? "true" : "false",
        },
      ],
      hashes: [{ label: "audit_open_hash", value: r.audit_open_hash }],
    },
  };
}

function buildAuditClaim(label: string, hash: string, acked: boolean): Claim {
  return {
    label,
    verdict: acked ? "attested" : "verified",
    evidence: {
      specRef: "Part VI §38 — hash-chained audit ledger",
      source: "audit-pair entry (referenced by the dispatch receipt)",
      hashes: [{ label: acked ? "audit_ack_hash" : "audit_open_hash", value: hash }],
      detail: [
        {
          label: "note",
          value: acked
            ? "ACK entry hash — the audit pair is closed (delivery completed)."
            : "Opening entry hash — recorded when the dispatch was accepted.",
        },
      ],
    },
  };
}

function buildDeliveryClaim(r: DispatchReceipt, delivered: boolean): Claim {
  return {
    label: delivered
      ? "Store-and-forward delivery completed"
      : "Store-and-forward delivery pending",
    verdict: delivered ? "verified" : "pending",
    evidence: {
      specRef: "Part IV §29 — store-and-forward",
      source: `GET /v1/dispatch/${r.request_id}`,
      hashes: [
        { label: "audit_open_hash", value: r.audit_open_hash },
        { label: "audit_ack_hash", value: r.audit_ack_hash },
      ],
      detail: [
        {
          label: "ack sentinel",
          value: delivered
            ? "real hash present → delivered"
            : `all-zeros (${ZERO_HASH.slice(0, 8)}…) → queued, not yet delivered`,
        },
      ],
    },
  };
}

/* ------------------------------------------------------------------ */
/* Small utilities                                                     */
/* ------------------------------------------------------------------ */

function readScalar(spec: SeriesSpec, samples: MetricSample[]): number | null {
  const v = spec.select(samples);
  return Number.isFinite(v) ? v : null;
}

function renderSample(s: MetricSample): string {
  const labels = Object.entries(s.labels);
  const labelStr =
    labels.length > 0
      ? `{${labels.map(([k, v]) => `${k}="${v}"`).join(",")}}`
      : "";
  return `${s.name}${labelStr} ${s.value}`;
}

function readHashTail(): string {
  // #/transport/<request_id> → "<request_id>"
  const tail = window.location.hash
    .replace(/^#\/?/, "")
    .split("/")
    .slice(1)
    .join("/");
  try {
    return decodeURIComponent(tail);
  } catch {
    return tail;
  }
}

function fmtAgo(ts: number): string {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 2) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  return `${m}m ago`;
}
